import { DashboardBookingCard } from '@/features/consumer/dashboard-booking-card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/auth-context';
import { useGetBookingQuery } from '@/hooks/queries/use-get-booking.query';
import { usePaginatedBookings } from '@/hooks/use-paginated-list';
import { Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';

export function UpcomingCleaningWidgetDashboard() {
  const { user } = useAuth();
  const {
    data: bookingsData,
    isLoading,
    error,
  } = useGetBookingQuery(user?.userid || '', {
    status: 'upcoming',
    history: 'upcoming',
    sort_by: 'booking_date',
    sort_order: 'asc',
  });

  const bookings = bookingsData?.bookings || [];
  const { visibleBookings, loadMore, canLoadMore } =
    usePaginatedBookings(bookings);

  const sectionTitle = (
    <h2 className="text-lg font-bold text-black dark:text-white">
      Upcoming Appointments
    </h2>
  );

  if (isLoading) {
    return (
      <section className="space-y-4">
        {sectionTitle}
        <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white py-12 dark:border-neutral-800 dark:bg-neutral-950">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading...
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="space-y-4">
        {sectionTitle}
        <div className="py-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            Failed to load bookings. Please try again.
          </p>
        </div>
      </section>
    );
  }

  if (bookings.length === 0) {
    return (
      <section className="space-y-4">
        {sectionTitle}
        <Card className="rounded-2xl border border-gray-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
          <div className="py-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No upcoming bookings found.
            </p>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      {sectionTitle}
      <div className="flex flex-col gap-4">
        {visibleBookings.map(booking => (
          <DashboardBookingCard
            key={booking.booking_id}
            booking={booking}
            variant="upcoming"
            showReviewAndTip={false}
          />
        ))}
        {canLoadMore ? (
          <div className="flex justify-center pt-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-[#ED7034] bg-transparent px-8 text-[#ED7034] hover:bg-[#ED7034]/10 hover:text-[#ED7034] dark:hover:bg-[#ED7034]/20"
              onClick={loadMore}
            >
              Load more
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
